import { type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Input, Skeleton } from '@/components/ui';
import { useAuditLog, type AuditEntry } from '@/hooks/useAdminAudit';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Admin → Audit log. List + filter view of every admin mutation captured
// by middleware.AuditAdmin. Reseller-scoped on the server.
//
// Filter strategy: small set of inputs at the top, no auto-debounce —
// the operator hits "Apply" or presses Enter. Keeps the UI predictable
// and avoids hammering the API on every keystroke.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;
const METHOD_OPTIONS = ['', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

export default function AdminAuditPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  // Working state for the filter form. Applied state is what we send
  // to the query hook — separating them keeps the UI from refetching
  // on every keystroke.
  const [method, setMethod] = useState<string>('');
  const [pathPrefix, setPathPrefix] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [page, setPage] = useState(0);
  const [appliedFilter, setAppliedFilter] = useState<{
    method: string;
    path: string;
    email: string;
  }>({ method: '', path: '', email: '' });

  const filter = useMemo(
    () => ({
      method: appliedFilter.method,
      path: appliedFilter.path,
      email: appliedFilter.email,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [appliedFilter, page],
  );

  const list = useAuditLog(filter);
  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const apply = (): void => {
    setPage(0);
    setAppliedFilter({ method, path: pathPrefix.trim(), email: email.trim() });
  };
  const clear = (): void => {
    setMethod('');
    setPathPrefix('');
    setEmail('');
    setPage(0);
    setAppliedFilter({ method: '', path: '', email: '' });
  };

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
          {t('aud_title')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1.5">{t('aud_sub')}</p>
      </header>

      <Card padding="md" className="mb-4">
        <form
          className="grid grid-cols-1 md:grid-cols-[140px_1fr_1fr_auto_auto] gap-3 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
        >
          <Field label={t('aud_field_method')}>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
            >
              {METHOD_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m === '' ? t('aud_field_any') : m}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('aud_field_path')}>
            <Input
              value={pathPrefix}
              onChange={(e) => setPathPrefix(e.target.value)}
              placeholder="/v1/admin/rate-cards"
              className="mono"
            />
          </Field>
          <Field label={t('aud_field_email')}>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alice@"
            />
          </Field>
          <Button type="submit">{t('aud_apply')}</Button>
          <Button type="button" variant="secondary" onClick={clear}>
            {t('aud_clear')}
          </Button>
        </form>
      </Card>

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('aud_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1 max-w-[460px] mx-auto">
              {t('aud_empty_sub')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-4 py-2.5 font-medium">{t('aud_col_when')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('aud_col_actor')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('aud_col_method')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('aud_col_path')}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t('aud_col_status')}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t('aud_col_duration')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} lang={lang} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-[13px]">
          <span className="text-text-3">
            {t('aud_pagination', {
              from: page * PAGE_SIZE + 1,
              to: Math.min((page + 1) * PAGE_SIZE, total),
              total: total.toLocaleString(lang),
            })}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              {t('aud_prev')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              {t('aud_next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface AuditRowProps {
  entry: AuditEntry;
  lang: string;
}
function AuditRow({ entry, lang }: AuditRowProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ok = entry.status_code >= 200 && entry.status_code < 300;
  const hasBody = entry.request_body !== undefined && entry.request_body !== null;

  return (
    <>
      <tr
        className={cn('border-b border-line last:border-0 hover:bg-surface-2 cursor-pointer', open && 'bg-surface-2')}
        onClick={() => hasBody && setOpen((v) => !v)}
        title={hasBody ? 'Click to view request body' : undefined}
      >
        <td className="px-4 py-3 text-text-3 text-[12px] whitespace-nowrap">
          {formatRelativeTime(entry.created_at, lang)}
        </td>
        <td className="px-4 py-3 text-text">{entry.actor_email}</td>
        <td className="px-4 py-3">
          <span className="mono text-[11px] font-semibold text-text-2">{entry.method}</span>
        </td>
        <td className="px-4 py-3 mono text-[12px] text-text-2 break-all">{entry.path}</td>
        <td className="px-4 py-3 num text-right">
          <span
            className={cn(
              'mono text-[12px] font-medium',
              ok ? 'text-success' : 'text-danger',
            )}
          >
            {entry.status_code}
          </span>
        </td>
        <td className="px-4 py-3 num text-right text-text-3 text-[12px]">{entry.duration_ms}ms</td>
      </tr>
      {open && hasBody && (
        <tr className="border-b border-line bg-surface-2">
          <td colSpan={6} className="px-4 py-3">
            <pre className="mono text-[11px] text-text-3 whitespace-pre-wrap break-all max-h-[280px] overflow-auto">
              {JSON.stringify(entry.request_body, null, 2)}
            </pre>
            <div className="mt-2 flex items-center gap-3 text-[11px] text-text-4">
              {entry.request_id && (
                <span>
                  request_id: <span className="mono">{entry.request_id}</span>
                </span>
              )}
              {entry.ip_address && (
                <span>
                  ip: <span className="mono">{entry.ip_address}</span>
                </span>
              )}
              {entry.user_agent && (
                <span className="truncate max-w-[400px]">UA: {entry.user_agent}</span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

